
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const VALoanPCSPage = () => {
  const getIsoDate = () => {
    try {
      return new Date().toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="VA Loan Benefits for PCS Permanent Change of Station | Fort Bliss El Paso TX | TREX RE LLC"
        description="Complete guide to VA loan benefits during a Permanent Change of Station (PCS). Learn about entitlement restoration, concurrent VA loans, military moving assistance, army relocation benefits, BAH strategy, SCRA protections, selling or renting your home during PCS from Fort Bliss El Paso Texas."
        keywords="pcs relocation va loan benefits, military moving assistance, army relocation assistance, military moving benefits, military retirement moving benefits, financial assistance for veterans moving, fort bliss, el paso texas, va loan pcs"
        canonical="/military/va-loan-pcs-permanent-change-of-station"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "VA Loan Benefits for a Permanent Change of Station (PCS): Complete Military Relocation Guide",
          "description": "Comprehensive guide to VA loan benefits during PCS. Covers entitlement restoration, concurrent VA loans, military moving assistance, army relocation benefits, BAH strategy, SCRA protections, and selling or renting your home during PCS from Fort Bliss El Paso Texas.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "datePublished": "2025-01-28",
          "dateModified": getIsoDate(),
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://trexrealestate.com/military/va-loan-pcs-permanent-change-of-station"
          }
        }}
      />

      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2227059e50f2075d0695edda1bcbf55a.jpg"
            alt="Military family relocating during PCS from Fort Bliss El Paso Texas with VA loan benefits"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
            VA Loan &amp; PCS Relocation Guide
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            VA Loan Benefits for a Permanent Change of Station (PCS)
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
            Everything military families need to know about using VA loan benefits during a PCS. Entitlement restoration, concurrent loans, selling vs. renting, military moving assistance, BAH strategy, SCRA protections, and financial assistance for veterans moving from Fort Bliss El Paso Texas.
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>By TREX RE LLC</span>
            <span>&bull;</span>
            <span>January 2025</span>
            <span>&bull;</span>
            <span>22 min read</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">In This Guide</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <a href="#va-loan-entitlement" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>VA Loan Entitlement During PCS</span>
            </a>
            <a href="#selling-during-pcs" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Selling Your Home During PCS</span>
            </a>
            <a href="#keeping-as-rental" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Keeping Your Home as a Rental</span>
            </a>
            <a href="#moving-benefits" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Military Moving Benefits &amp; Assistance</span>
            </a>
            <a href="#buying-new-station" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Buying at Your New Duty Station</span>
            </a>
            <a href="#concurrent-va-loans" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Holding Two VA Loans Simultaneously</span>
            </a>
            <a href="#bah-strategy" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>BAH Strategy &amp; Housing Allowance</span>
            </a>
            <a href="#scra-protections" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>SCRA Protections &amp; Legal Rights</span>
            </a>
            <a href="#retirement-separation" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>VA Loan After Retirement or Separation</span>
            </a>
            <a href="#common-mistakes" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Common Mistakes to Avoid</span>
            </a>
            <a href="#faq" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>FAQ: VA Loans &amp; PCS Moves</span>
            </a>
          </div>
        </div>
      </section>

      <ArticlePartOne />
      <ArticlePartTwo />
      <ArticlePartThree />
      <ArticlePartFour />

      <Footer />
    </div>
  );
};

export default VALoanPCSPage;
